import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { P, ToolTip } from "../AbstractElements";
import { BEHAVIOURAL_ASSESSMENT, ASSESSMENT_CRUD} from "../Api/apiUtils";

type BehaviouralParam = {
  id: number;
  minPoints: number;
  maxPoints: number;
  points?: number;
  question: string;
};

type BehaviouralParams = {
  [key: string]: BehaviouralParam[];
};

type Scores = {
  [key: string]: number;
};

type BehaviouralFormProps = {
  onComplete: () => void;
  courseId: number;
  onBack?: () => void;
  eventDetails: {
    courseId: number;
    studentUserId: number;
    classNumber: number;
    classId: number;
    // add levelId if needed
    [key: string]: any;
  };
  levelId: number;
};

const BehaviouralForm: React.FC<BehaviouralFormProps> = ({
  onComplete,
  courseId,
  onBack,
  eventDetails,
  levelId,
}) => {
  console.log("levelId",levelId)
  const [params, setParams] = useState<BehaviouralParams | null>(null);
  const [scores, setScores] = useState<Scores>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchParams() {
      setLoading(true);
      try {
        const res = await BEHAVIOURAL_ASSESSMENT(courseId);
        setParams(res.data.data); // <-- Fix here
      } finally {
        setLoading(false);
      }
    }
    fetchParams();
  }, [courseId]);

  if (loading) return <div>Loading...</div>;
  if (!params) return <div>No data</div>;

  const paramNames = Object.keys(params);

  const handleScore = (key: string, value: number) => {
    setScores((prev) => ({ ...prev, [key]: value }));
  };

  const allFilled = paramNames.every((p) => scores[p]);

  const handleSubmit = async () => {
    setLoading(true);
    onComplete();
    try {
      // Build the behavioural object
      const behavioural: { [key: string]: BehaviouralParam[] } = {};
      paramNames.forEach(name => {
        const param = params[name][0];
        behavioural[name] = [{
          id: param.id,
          minPoints: param.minPoints,
          maxPoints: param.maxPoints,
          points: scores[name] || 0,
          question: param.question
        }];
      });

      // Calculate scores
      const behaviouralScore = paramNames.reduce((sum, name) => sum + (scores[name] || 0), 0);
      const maxBehaviouralScore = paramNames.reduce((sum, name) => sum + (params[name][0].maxPoints || 0), 0);

      // Prepare payload
      const payload = {
        courseId: eventDetails.courseId,
        userId: eventDetails.studentUserId,
        classCount: eventDetails.classNumber,
        levelId:levelId,
        classScheduleId: eventDetails.classId,
        responseData: { behavioural },
        behaviouralScore,
        maxBehaviouralScore
      };

      // POST to ASSESSMENT_CRUD
      await ASSESSMENT_CRUD("behavioural", payload);

      // Now call onComplete (to mark class as complete, etc)

    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, minWidth: 350 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        {onBack && (
          <span
            onClick={onBack}
            style={{
              cursor: "pointer",
              fontSize: 24,
              marginRight: 12,
              userSelect: "none",
              fontWeight: 700,
              lineHeight: 1,
            }}
            title="Back"
          >
            ←
          </span>
        )}
        <P style={{ margin: 0 }}>
          <b>Behavioural Report</b>
        </P>
      </div>
      <P>
        Please make sure to fill the behavioural report before completing the class
      </P>
      {paramNames.map((name) => {
  const param = params[name][0];
  const safeId = `tooltip-${name.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <div key={name} style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
      <span
  id={safeId}
  style={{
    marginRight: 8,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#f3f3f3",
    border: "1px solid #bbb",
    fontWeight: 700,
    fontSize: 16,
    color: "#333",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  }}
>
  i
</span>
        <ToolTip target={safeId} placement="top">
          {param?.question}
        </ToolTip>
        <h4>{name}</h4>
      </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[...Array(param.maxPoints)].map((_, i) => (
                <Button
                  key={i + 1}
                  color={scores[name] === i + 1 ? "primary" : "light"}
                  onClick={() => handleScore(name, i + 1)}
                  style={{ minWidth: 36, minHeight: 36, padding: 0 }}
                >
                  {i + 1}
                </Button>
              ))}
            </div>
          </div>
        );
      })}
      <Button
        color="secondary"
        block
        disabled={!allFilled || loading}
        onClick={handleSubmit}
        style={{ marginTop: 24, fontWeight: 600, fontSize: 18, borderRadius: 20, position: "relative" }}
      >
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ marginRight: 8, verticalAlign: "middle" }}
            />
            Completing...
          </>
        ) : (
          "Complete Class"
        )}
      </Button>
    </div>
  );
};

export default BehaviouralForm;